import { debounce, requestIdleCallback } from '@theme/utilities';

const GROUP_SELECTOR = '[data-match-height-group]';
const DEFAULT_ITEM_SELECTOR = '[data-match-height-item]';
const groups = new WeakMap();

export class MatchHeightGroup {
  /**
   * @param {HTMLElement} element
   */
  constructor(element) {
    this.element = element;
    this.itemSelector = element.getAttribute('data-match-height-target') || DEFAULT_ITEM_SELECTOR;

    this.update = this.update.bind(this);
    this.#handleResize = debounce(this.update, 100);
    this.#handleMutations = debounce(this.refresh.bind(this), 50);

    this.#mutationObserver = new MutationObserver(() => this.#handleMutations());
    this.#mutationObserver.observe(this.element, {
      childList: true,
      subtree: true,
    });

    this.#resizeObserver = new ResizeObserver(() => this.update());

    window.addEventListener('resize', this.#handleResize);

    this.refresh();
  }

  refresh() {
    this.items = [...this.element.querySelectorAll(this.itemSelector)];

    this.#resizeObserver.disconnect();
    this.items.forEach((item) => this.#resizeObserver.observe(item));

    this.update();
  }

  update() {
    const visibleItems = this.items.filter((item) => item.offsetParent !== null);
    if (!visibleItems.length) return;

    visibleItems.forEach((item) => {
      item.style.minHeight = '';
    });

    let maxHeight = 0;
    visibleItems.forEach((item) => {
      const height = item.getBoundingClientRect().height;
      if (height > maxHeight) maxHeight = height;
    });

    const heightValue = Math.ceil(maxHeight);
    visibleItems.forEach((item) => {
      item.style.minHeight = `${heightValue}px`;
    });
  }

  destroy() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
    window.removeEventListener('resize', this.#handleResize);
    this.items.forEach((item) => {
      item.style.minHeight = '';
    });
  }

  /** @type {HTMLElement} */
  element;
  /** @type {HTMLElement[]} */
  items = [];
  /** @type {string} */
  itemSelector;

  /** @type {ResizeObserver} */
  #resizeObserver;
  /** @type {MutationObserver} */
  #mutationObserver;
  /** @type {() => void} */
  #handleResize;
  /** @type {() => void} */
  #handleMutations;
}

function initGroup(element) {
  if (groups.has(element)) return;
  groups.set(element, new MatchHeightGroup(element));
}

function destroyGroup(element) {
  const instance = groups.get(element);
  if (!instance) return;
  instance.destroy();
  groups.delete(element);
}

function scan(root = document) {
  if (root instanceof Element && root.matches(GROUP_SELECTOR)) {
    initGroup(root);
  }

  root.querySelectorAll?.(GROUP_SELECTOR).forEach((element) => initGroup(element));
}

function teardown(root) {
  if (!(root instanceof Element)) return;

  if (root.matches(GROUP_SELECTOR)) {
    destroyGroup(root);
  }

  root.querySelectorAll(GROUP_SELECTOR).forEach((element) => destroyGroup(element));
}

function init() {
  scan(document);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => scan(node));
      mutation.removedNodes.forEach((node) => teardown(node));
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    requestIdleCallback(init);
  });
} else {
  requestIdleCallback(init);
}

window.MatchHeightGroup = MatchHeightGroup;
